/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/

import * as React from 'react';

import { getFallbackImageUrl } from '@msdyn365-commerce-modules/retail-actions';
import { IImageSettings, Image } from '@msdyn365-commerce/core';
import _getViewPort from '../../Utilities/get-view-port';
import { IMfrmProductDescriptionData } from './mfrm-product-description.data';
import { IMfrmProductDescriptionProps } from './mfrm-product-description.props.autogenerated';

export interface IMfrmProductDescriptionViewProps extends IMfrmProductDescriptionProps<IMfrmProductDescriptionData> {
    state: IMfrmProductDescriptionState;
    toggle: React.MouseEventHandler;
    mediaContent: string;
    customText: string;
    spotlightTitle: IMfrmProductDescriptionContent[];
    noContent: boolean;
    forceDisplay: boolean;
}

export interface IMfrmProductDescriptionContent {
    label: string;
    icon: string;
}

export interface IMfrmProductDescriptionState {
    isOpen: boolean;
    isMobileTablet: boolean;
    isDesktop: boolean;
}
/**
 *
 * MfrmProductDescriptionFeatureSpotight component
 * @extends {React.PureComponent<IMfrmProductDescriptionProps<IMfrmProductDescriptionFeatureSpotightData>>}
 */
class MfrmProductDescription extends React.PureComponent<
    IMfrmProductDescriptionProps<IMfrmProductDescriptionData>,
    IMfrmProductDescriptionState
    > {
    constructor(props: IMfrmProductDescriptionProps<IMfrmProductDescriptionData>) {
        super(props);
        this.state = {
            isDesktop: false,
            isMobileTablet: false,
            isOpen: false
        };
    }

    public componentDidMount(): void {
        const viewPort = _getViewPort(this.props.context);
        (viewPort === 'xs' || viewPort === 'sm' || viewPort === 'md') ? this.setState({ isMobileTablet: true }) : this.setState({ isDesktop: true });
        (viewPort === 'lg' || viewPort === 'xl') ? this.setState({ isDesktop: true }) : this.setState({ isMobileTablet: true });
    }

    public _toggle = () => {
        this.setState({ isOpen: !this.state.isOpen });
    };

    public _renderImageHost = (url: string) => {
        let fallbackImageUrl;
        const defaultImageSettings: IImageSettings = {
            viewports: {
                xs: { q: `w=95&h=76&m=6`, w: 0, h: 0 }
            },
            disableLazyLoad: true
        };
        if (this.props.data.product.result && this.props.data.productSpecificationData.result !== undefined) {
            fallbackImageUrl = getFallbackImageUrl(
                this.props.data.product.result.ItemId,
                this.props.context.actionContext.requestContext.apiSettings
            );
        }
        return (
            <>
                <Image
                    src={url || ''}
                    imageSettings={defaultImageSettings}
                    gridSettings={this.props.context.request.gridSettings!}
                    altText={url}
                    loadFailureBehavior='empty'
                    fallBackSrc={fallbackImageUrl}
                />
            </>
        );
    };

    public _renderVideoPlayer = (name: string) => {
        return (
            <>
                {/* <MsDyn365.Video>
                    <Player playerData={name} />
                </MsDyn365.Video> */}
                {name}
            </>
        );
    };

    // tslint:disable-next-line: cyclomatic-complexity
    public render(): JSX.Element | null {
        const SpotlightText = this.props.config.spotlightKey;
        const features: IMfrmProductDescriptionContent[] = [];
        let mediaContent;
        let customDescription;
        let noContent = true;
        let forceDisplay = false;
        if (this.props.data.productSpecificationData.result && this.props.data.productSpecificationData.result !== undefined) {
            const attributes = this.props.data.productSpecificationData.result;
            const spotlightAttributeKey = attributes[attributes.findIndex(obj => obj.Name === SpotlightText)];
            const spotlightValues = spotlightAttributeKey?.TextValue ? spotlightAttributeKey?.TextValue : undefined;
            if (spotlightValues !== undefined) {
                const spotlightValuesArr = spotlightValues && spotlightValues.split(',');
                spotlightValuesArr &&
                    spotlightValuesArr.length > 0 &&
                    spotlightValuesArr.map((item, index) => {
                        const attribute = attributes && attributes[attributes.findIndex(obj => obj.Name === item)];
                        if (attribute !== undefined && attribute.BooleanValue === true && features.length < 8) {
                            const iconText = item?.replace(/ /g, '_').toLowerCase();
                            const obj = {
                                label: item,
                                icon: iconText
                            };
                            features.push(obj);
                            noContent = false;
                        }
                    });
            }

            const attributeKey = attributes[attributes.findIndex(obj => obj.Name === 'enhanced_content')];
            const enhancedContent = attributeKey?.TextValue ? attributeKey?.TextValue : undefined;
            if (enhancedContent !== undefined) {
                const enhancedContentSplit = enhancedContent && enhancedContent.split(' | ');
                if (enhancedContentSplit && enhancedContentSplit.length > 0) {
                    if (enhancedContentSplit[0] === 'interactable') {
                        mediaContent = enhancedContentSplit[1];
                    } else if (enhancedContentSplit[0] === 'image') {
                        if (enhancedContentSplit[1].includes('url=') === true) {
                            const explodeUrl = enhancedContentSplit[1].split('=');
                            mediaContent = this._renderImageHost(
                                `${this.props.context.request.apiSettings.baseImageUrl}Products/${explodeUrl[1]}`
                            );
                            forceDisplay = true;
                        } else {
                            mediaContent = enhancedContentSplit[1];
                        }
                    } else if (enhancedContentSplit[0] === 'video') {
                        if (enhancedContentSplit[1].includes('url=') === true) {
                            // when cms hosted video will be available we need to add logic here
                            // const explodeUrl = enhancedContentSplit[1].split('=');
                        } else {
                            mediaContent = enhancedContentSplit[1];
                        }
                    }
                }
            }

            const customDescriptionAttributeKey = attributes[attributes.findIndex(obj => obj.Name === 'customDescription')];
            customDescription = customDescriptionAttributeKey?.TextValue ? customDescriptionAttributeKey?.TextValue : undefined;
            if (mediaContent !== undefined || customDescription !== undefined) {
                noContent = false;
                const enhancedContentSplit = customDescription && customDescription.split(' | ');
                if (enhancedContentSplit && enhancedContentSplit.length > 0) {
                    customDescription = enhancedContentSplit[0];
                    mediaContent = enhancedContentSplit[1] !== undefined ? enhancedContentSplit[1] : mediaContent;
                }
            }
        }

        const viewProps = {
            ...this.props,
            spotlightTitle: features,
            mediaContent: mediaContent,
            customText: customDescription,
            state: this.state,
            toggle: this._toggle,
            noContent: noContent,
            forceDisplay: forceDisplay
        };
        return this.props.renderView(viewProps);
    }
}

export default MfrmProductDescription;
